import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  distinct,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  throttleTime,
} from 'rxjs/operators';
import { Set } from 'immutable';
import { requestActions } from './apiHelpers';
import Contact from '../Models/Contact';
import { AppState } from '@reducers/rootReducer';
import { createNote } from './notes';
import { GroupActionSource, ID, ReduxAction } from '../types';
import { closeModal, redirectLocation } from './ui';
import { apiObservable, searchApiObservable } from '@support/api';
import { alertUser } from './alerts';
import { selectUser } from '@reducers/user/account';

/* Epic actions */
const newContactActions = requestActions('NEW_CONTACT', ['update']);
const contactActions = requestActions('CONTACT', ['fetch', 'create', 'delete', 'update']);
const throttledContactActions = requestActions('THROTTLED_CONTACT', ['fetch']);
const contactInviteActions = requestActions('CONTACT_INVITE', ['create']);
const contactBatchActions = requestActions('CONTACT_BATCH', ['delete', 'merge']);
const elasticsearchContactActions = requestActions('ELASTICSEARCH_CONTACT', ['fetch']);

/* Component actions */
export const fetchContact = contactActions['fetch'];
export const fetchContactThrottled = throttledContactActions['fetch'];
export const deleteContactBatch = (contact_ids: Array<ID>, source: GroupActionSource) =>
  contactBatchActions['delete']({ contact_ids, source });
export const mergeContactBatch = (contact_ids: Array<ID>, source: GroupActionSource) =>
  contactBatchActions['merge']({ contact_ids, source });

export const inviteContact = (contact: Contact) => ({
  type: 'INVITE_CONTACT',
  payload: { contact },
});

// This is for editing a new contact in the store prior to posting. We maintain state
// in the "New Person" view for the duration of a user's session.
export const updateNewContact = newContactActions['update'];
export const createContact = contactActions['create'];

// Possible use cases:
// 1. We have a contact results from an search and we may want to cache a selected
//    result, e.g. when adding a new mention in a note
// 2. We need to update a contact in the UI without dispatching a new API request and we'd
//    like that data to be available for the duration of a user's session.
export const updateContact = (contact) => ({ type: 'UPDATE_CONTACT_SUCCEEDED', payload: contact });

// Action creators to update store while supporting legacy code
export const deleteContactRequested = contactActions['deleteRequested'];
export const deleteContactSucceeded = contactActions['deleteSucceeded'];
export const deleteContactFailed = contactActions['deleteFailed'];
export const mergeContactsSucceeded = contactBatchActions['mergeSucceeded'];
export const updateContactSucceeded = (contact) => ({
  type: 'UPDATE_CONTACT_SUCCEEDED',
  payload: contact,
});

/* Selectors */
export const selectAllContacts = (state: AppState) => state.data.contacts.collection.items;
export const selectContactGetter = (state: AppState) => (contactID: ID) =>
  state.data.contacts.collection.items.get(String(contactID));
export const selectContact = (contactID: ID, fallbackValue?: Contact) => (state: AppState) =>
  state.data.contacts.collection.items.get(
    String(contactID),
    fallbackValue === undefined ? new Contact() : fallbackValue,
  );
export const selectMergingContactBatch = (state: AppState) =>
  state.data.contacts.mergingContactBatch;

export const selectContactArray = (contactIDs: Array<ID>) => (state: AppState) =>
  contactIDs.map((contactID) => selectContact(contactID)(state));

export const selectContactsForIDs = (contactIDSet: Set<ID>) => (state: AppState) => {
  return contactIDSet
    .map((contactID) => state.data.contacts.collection.items.get(String(contactID)))
    .toArray()
    .filter((contact) => !!contact);
};

export const createContactEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CONTACT'),
    mergeMap(({ payload }) => {
      const note = payload.note;
      return apiObservable('POST', `/network/contacts/`, {
        person_lookup: payload.contact.displayName,
      }).pipe(
        map((response) => contactActions['createSucceeded']({ ...response, note })),
        startWith(contactActions['createRequested']()),
        catchError((err) => of(contactActions['createFailed'](err))),
      );
    }),
  );

export const createNewContactNoteEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_CONTACT_SUCCEEDED'),
    // Only POST new note if a note is included and we have a contact ID in response
    filter(({ payload }) => payload.response.id && payload.note),
    map(({ payload }) => {
      const contact = new Contact(payload.response);
      return createNote(payload.note.copyWith({ contact }));
    }),
  );

export const fetchContactEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_CONTACT'),
    filter(({ payload }) => !['paddingRow', 'sectionHeader'].includes(payload.id)),
    mergeMap(({ payload }) =>
      apiObservable('GET', `/network/contacts/${payload.id}`).pipe(
        map(({ response }) => {
          return contactActions['fetchSucceeded']({
            contact: response,
            pageSource: payload.pageSource,
          });
        }),
        startWith(contactActions['fetchRequested']({ id: payload.id })),
        catchError((err) => of(contactActions['fetchFailed']({ ...err, contactID: payload.id }))),
      ),
    ),
  );

const fetchContactThrottledEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_THROTTLED_CONTACT'),
    filter(({ payload }) => !['paddingRow', 'sectionHeader'].includes(payload.id)),
    groupBy(({ payload }) => payload.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given contact, only fetch the cadence at most once every 1 minute
        throttleTime(1000 * 60 * 1),
        mergeMap(({ payload }) =>
          apiObservable('GET', `/network/contacts/${payload.id}`).pipe(
            map(({ response }) => {
              return contactActions['fetchSucceeded']({
                contact: response,
                pageSource: payload.pageSource,
              });
            }),
            startWith(contactActions['fetchRequested']({ id: payload.id })),
            catchError((err) =>
              of(contactActions['fetchFailed']({ ...err, contactID: payload.id })),
            ),
          ),
        ),
      ),
    ),
  );

// Only emit once per contact ID per user session. All other fetches for contacts will
// emit requests to the Clay API.
export const fetchTypesenseContactEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_CONTACT'),
    filter(({ payload }) => !['paddingRow', 'sectionHeader'].includes(payload.id)),
    distinct(({ payload }) => String(payload.id)),
    mergeMap(({ payload }) =>
      searchApiObservable('GET', `contact/${payload.id}`, {
        user: selectUser(state$.value),
      }).pipe(
        map((response) =>
          elasticsearchContactActions['fetchSucceeded']({ ...response, contactID: payload.id }),
        ),
        startWith(elasticsearchContactActions['fetchRequested']({ id: payload.id })),
        catchError((err) =>
          of(elasticsearchContactActions['fetchFailed']({ ...err, contactID: payload.id })),
        ),
      ),
    ),
  );

export const deleteContactBatchEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_CONTACT_BATCH'),
    mergeMap(({ payload }) =>
      apiObservable('DELETE', `/network/contacts/`, payload).pipe(
        map(() => contactBatchActions['deleteSucceeded'](payload)),
        startWith(contactBatchActions['deleteRequested'](payload)),
        catchError((err) => of(contactBatchActions['deleteFailed']({ ...err, ...payload }))),
      ),
    ),
  );

export const mergeContactBatchEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MERGE_CONTACT_BATCH'),
    mergeMap(({ payload }) =>
      apiObservable('POST', `/network/contacts/merge/`, {
        contact_ids: payload.contact_ids,
        wait_for_index: false,
      }).pipe(
        map((response) =>
          contactBatchActions['mergeSucceeded']({
            ...response,
            mergedContactIDs: payload.contact_ids,
            source: payload.source,
            // @ts-ignore
            contactID: response.response?.contact?.objectID,
          }),
        ),
        startWith(contactBatchActions['mergeRequested'](payload)),
        catchError((err) => {
          // We should display this in a custom modal. This is a stopgap that matches our
          // current merge modal functionality.
          alert('Something went wrong with the merge, please try again.');
          return of(
            contactBatchActions['mergeFailed']({
              ...err,
              failedMergeIDs: payload.contact_ids,
            }),
          );
        }),
      ),
    ),
  );

// After a successful merge, if the path is a contact detail view, and the ID in the URL is
// matched in the merged contacts, and that ID is not the ID in the URL, navigate to the returned,
// unified contact. Always close the modal if it is still open.
export const mergeContactBatchSucceededEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) => {
  return action$.pipe(
    ofType('MERGE_CONTACT_BATCH_SUCCEEDED'),
    mergeMap(({ payload }) => {
      const currentView = state$.value.ui.location.view;
      const currentPath = state$.value.ui.location.pathname;

      if (payload.contactID && currentView.includes('Contact')) {
        const contactIDInView = currentPath.split('/').slice(-1)[0];
        const { mergedContactIDs, contactID } = payload;

        if (mergedContactIDs.includes(contactID) && contactID != contactIDInView) {
          return of(redirectLocation({ pathname: `/contact/${contactID}` }));
        }
      }
      return of(closeModal);
    }),
  );
};

export const inviteContactEpic = (action$: Observable<ReduxAction>) => {
  return action$.pipe(
    ofType('INVITE_CONTACT'),
    mergeMap(({ payload }) => {
      return apiObservable(
        'POST',
        `/contact-invite/`,
        { contact: payload.contact.id, email: payload.contact.email.split(',')[0] },
        { apiVersion: 'v2' },
      ).pipe(
        map((response) => contactInviteActions['createSucceeded']({ ...payload, ...response })),
        startWith(contactInviteActions['createRequested'](payload)),
        catchError((error) => of(contactInviteActions['createFailed']({ error, ...payload }))),
      );
    }),
  );
};

export const alertUserOnInviteSucceededEpic = (action$: Observable<ReduxAction>) => {
  return action$.pipe(
    ofType('CREATE_CONTACT_INVITE_SUCCEEDED'),
    map(({ payload }) => {
      return alertUser(`Invitation sent to ${payload.contact.email}`, 'success');
    }),
  );
};

export const alertUserOnInviteFailedEpic = (action$: Observable<ReduxAction>) => {
  return action$.pipe(
    ofType('CREATE_CONTACT_INVITE_FAILED'),
    map(({ payload }) => {
      return alertUser(`Failed to invite ${payload.contact.email}, try again`, 'error');
    }),
  );
};

const deleteContactAlertEpic = (action$: Observable<ReduxAction>) => {
  return action$.pipe(
    ofType('DELETE_CONTACT_SUCCEEDED', 'DELETE_CONTACT_BATCH_SUCCEEDED'),
    map(({ payload, type }) => {
      let message = 'Archived 1 Person';

      if (type.includes('BATCH') && payload.contact_ids?.length > 1) {
        message = `Archived ${payload.contact_ids?.length} People`;
      }

      return alertUser(message, 'success');
    }),
  );
};

// Epics to expire caching of deleted contacts and merged contacts
const expireDeletedContactCacheEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_CONTACT_SUCCEEDED', 'DELETE_CONTACT_BATCH_SUCCEEDED'),
    delay(1000 * 15),
    map(({ payload }) => {
      let contact_ids = payload.contact_ids;

      // Delete single contact case
      if (payload.id) {
        contact_ids = [String(payload.id)];
      }
      return { type: 'EXPIRE_DELETED_CONTACTS_CACHE', payload: { contact_ids } };
    }),
  );

const expireMergedContactCacheEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MERGE_CONTACT_BATCH_SUCCEEDED'),
    delay(1000 * 15),
    map(({ payload }) => ({ type: 'EXPIRE_MERGED_CONTACTS_CACHE', payload })),
  );

export const contactsEpic = combineEpics(
  createContactEpic,
  createNewContactNoteEpic,
  fetchContactEpic,
  fetchContactThrottledEpic,
  fetchTypesenseContactEpic,
  deleteContactBatchEpic,
  mergeContactBatchEpic,
  mergeContactBatchSucceededEpic,
  inviteContactEpic,
  alertUserOnInviteSucceededEpic,
  alertUserOnInviteFailedEpic,
  deleteContactAlertEpic,
  expireDeletedContactCacheEpic,
  expireMergedContactCacheEpic,
);
